import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  Download, FileJson, FileSpreadsheet, Search, X,
  Loader2, AlertTriangle, CheckCircle, ShieldAlert,
  CreditCard, User, Database, Eye, ChevronDown, ChevronUp
} from 'lucide-react';

const SOURCE_COLORS = {
  children: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  imported_students: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  pending_enrollments: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  bookings: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  users: 'bg-green-500/20 text-green-400 border-green-500/30',
  stripe_customer: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  stripe_metadata: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  stripe_payment_method: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
};

const SourceBadge = ({ source }) => {
  if (!source) return <span className="text-white/20 text-xs">-</span>;
  const color = SOURCE_COLORS[source] || 'bg-white/10 text-white/50';
  return <Badge className={`${color} text-[10px] px-1.5 py-0 border`}>{source}</Badge>;
};

const FieldCell = ({ field }) => {
  if (!field || !field.value) return <span className="text-white/20 text-xs">MISSING</span>;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-white text-sm">{field.value}</span>
      <SourceBadge source={field.source} />
    </div>
  );
};

const CompletenessIndicator = ({ completeness, flags }) => {
  const bg = completeness === 'complete' ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
    : completeness === 'partial' ? 'bg-amber-500/15 border-amber-500/30 text-amber-400'
    : 'bg-red-500/15 border-red-500/30 text-red-400';

  const icon = completeness === 'complete' ? <CheckCircle className="w-3.5 h-3.5" />
    : completeness === 'partial' ? <AlertTriangle className="w-3.5 h-3.5" />
    : <ShieldAlert className="w-3.5 h-3.5" />;

  return (
    <div className="space-y-1">
      <Badge className={`${bg} border text-xs flex items-center gap-1 w-fit`}>
        {icon} {completeness}
      </Badge>
      <div className="flex flex-wrap gap-1">
        {flags.map((flag, i) => (
          <Badge key={i} className={`text-[9px] px-1 py-0 ${
            flag.includes('payment-linked') ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' :
            flag.includes('class-linked') ? 'bg-teal-500/10 text-teal-400 border-teal-500/20' :
            flag.includes('safeguarding-present') ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
            'bg-red-500/10 text-red-300 border-red-500/20'
          } border`}>{flag}</Badge>
        ))}
      </div>
    </div>
  );
};

const StudentDetailPanel = ({ record }) => {
  if (!record) return null;

  return (
    <div className="bg-[#0a0a0a] border border-[#333] rounded-lg p-4 space-y-4" data-testid="student-detail-panel">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Student Info */}
        <div className="space-y-3">
          <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">Student Info</h4>
          <div className="space-y-2">
            <div><span className="text-white/40 text-xs">Name:</span> <FieldCell field={record.student_name} /></div>
            <div><span className="text-white/40 text-xs">DOB:</span> <FieldCell field={record.dob} /></div>
            <div><span className="text-white/40 text-xs">Gender:</span> <FieldCell field={record.gender} /></div>
            <div><span className="text-white/40 text-xs">Medical:</span> <FieldCell field={record.medical_conditions} /></div>
          </div>
        </div>

        {/* Guardian Info */}
        <div className="space-y-3">
          <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">Guardian / Parent</h4>
          <div className="space-y-2">
            <div><span className="text-white/40 text-xs">Name:</span> <FieldCell field={record.parent_name} /></div>
            <div><span className="text-white/40 text-xs">Email:</span> <FieldCell field={record.parent_email} /></div>
            <div><span className="text-white/40 text-xs">Phone:</span> <FieldCell field={record.guardian_phone} /></div>
          </div>
        </div>

        {/* Emergency & Second Guardian */}
        <div className="space-y-3">
          <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">Emergency / Safeguarding</h4>
          <div className="space-y-2">
            <div><span className="text-white/40 text-xs">Emergency Contact:</span> <FieldCell field={record.emergency_contact} /></div>
            <div><span className="text-white/40 text-xs">Emergency Phone:</span> <FieldCell field={record.emergency_phone} /></div>
            <div><span className="text-white/40 text-xs">2nd Guardian:</span> <FieldCell field={record.second_guardian_name} /></div>
            <div><span className="text-white/40 text-xs">2nd Guardian Email:</span> <FieldCell field={record.second_guardian_email} /></div>
            <div><span className="text-white/40 text-xs">2nd Guardian Phone:</span> <FieldCell field={record.second_guardian_phone} /></div>
          </div>
        </div>

        {/* Payment Info */}
        <div className="space-y-3">
          <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">Payment / Stripe</h4>
          <div className="space-y-2">
            <div><span className="text-white/40 text-xs">Stripe Customer:</span> <FieldCell field={record.stripe_customer_id} /></div>
            <div><span className="text-white/40 text-xs">Payment Method:</span> <FieldCell field={record.stripe_payment_method_id} /></div>
            {record.cards && record.cards.length > 0 ? (
              <div>
                <span className="text-white/40 text-xs">Cards:</span>
                {record.cards.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 mt-1">
                    <CreditCard className="w-3.5 h-3.5 text-indigo-400" />
                    <span className="text-white text-sm">{c.brand} ****{c.last4} ({c.exp})</span>
                    <SourceBadge source={c.source} />
                  </div>
                ))}
              </div>
            ) : <div><span className="text-white/40 text-xs">Cards:</span> <span className="text-red-400 text-xs">NONE</span></div>}
          </div>
        </div>
      </div>

      {/* Classes & Bookings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">Classes</h4>
          {record.classes && record.classes.length > 0 ? (
            record.classes.map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-white text-sm">{c.name}</span>
                <SourceBadge source={c.source} />
              </div>
            ))
          ) : <span className="text-white/20 text-xs">NO CLASSES FOUND</span>}
        </div>

        <div className="space-y-2">
          <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">Bookings</h4>
          {record.bookings && record.bookings.length > 0 ? (
            record.bookings.map((b, i) => (
              <div key={i} className="text-sm">
                <span className="text-white">{b.class_name || 'Unknown class'}</span>
                <span className="text-white/40 ml-2">£{b.amount || 0}</span>
                <Badge className="ml-2 text-[10px] bg-white/5 text-white/50">{b.payment_status}</Badge>
              </div>
            ))
          ) : <span className="text-white/20 text-xs">NO BOOKINGS FOUND</span>}
        </div>
      </div>

      {/* Internal IDs & Found In */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">Internal IDs</h4>
          <div className="text-xs font-mono text-white/40 space-y-1">
            <div>child_id: {record.internal_ids?.child_id?.value || <span className="text-red-400">NULL</span>}</div>
            <div>user_id: {record.internal_ids?.user_id?.value || <span className="text-red-400">NULL</span>}</div>
            <div>pe_id: {record.internal_ids?.pending_enrollment_id?.value || <span className="text-red-400">NULL</span>}
              {record.internal_ids?.pe_exists_in_db === false && record.internal_ids?.pending_enrollment_id?.value &&
                <Badge className="ml-1 text-[9px] bg-red-500/20 text-red-400 border border-red-500/30">MISSING FROM DB</Badge>
              }
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider">Found In</h4>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(record.found_in || {}).map(([source, found]) => (
              <Badge key={source} className={`text-[10px] px-1.5 py-0.5 border ${
                found ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'bg-[#111] text-white/20 border-[#222]'
              }`}>{source}: {found ? 'YES' : 'NO'}</Badge>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const DataExportTab = () => {
  const [loading, setLoading] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentResult, setStudentResult] = useState(null);
  const [searchingStudent, setSearchingStudent] = useState(false);

  const loadExport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/data/export-all');
      setExportData(res.data);
      toast.success(`Loaded ${res.data.summary?.total_records || 0} records`);
    } catch (error) {
      toast.error('Failed to load export: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const downloadJSON = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/data/export-all');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `upa_student_export_${new Date().toISOString().slice(0,19).replace(/[:-]/g,'')}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('JSON export downloaded');
    } catch (error) {
      toast.error('Failed to download JSON');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/data/export-all?format=csv', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `upa_student_export_${new Date().toISOString().slice(0,19).replace(/[:-]/g,'')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV export downloaded');
    } catch (error) {
      toast.error('Failed to download CSV');
    } finally {
      setLoading(false);
    }
  };

  const searchStudent = async () => {
    if (!studentSearch.trim()) return;
    setSearchingStudent(true);
    try {
      const res = await api.get(`/admin/data/export-student/${encodeURIComponent(studentSearch.trim())}`);
      setStudentResult(res.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(`No records found for "${studentSearch}"`);
        setStudentResult(null);
      } else {
        toast.error('Search failed');
      }
    } finally {
      setSearchingStudent(false);
    }
  };

  const filtered = exportData?.records?.filter(r => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const name = (r.student_name?.value || '').toLowerCase();
    const email = (r.parent_email?.value || '').toLowerCase();
    const parentName = (r.parent_name?.value || '').toLowerCase();
    return name.includes(q) || email.includes(q) || parentName.includes(q);
  }) || [];

  return (
    <div className="space-y-6" data-testid="data-export-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="font-heading text-xl font-semibold text-white flex items-center gap-2">
            <Database className="w-5 h-5 text-cyan-400" />
            Data Recovery Export
          </h2>
          <p className="text-sm text-white/50 mt-1">Read-only snapshot of all recoverable student/family data with source provenance</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={loadExport} disabled={loading} className="border-[#333] text-white hover:bg-[#222]" data-testid="load-export-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
            Load All
          </Button>
          <Button variant="outline" onClick={downloadJSON} disabled={loading} className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10" data-testid="download-json-btn">
            <FileJson className="w-4 h-4 mr-2" />
            JSON
          </Button>
          <Button variant="outline" onClick={downloadCSV} disabled={loading} className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10" data-testid="download-csv-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            CSV
          </Button>
        </div>
      </div>

      {/* Per-Student Search */}
      <Card className="bg-[#111] border-[#222]">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <User className="w-4 h-4 text-cyan-400" />
            Per-Student Lookup
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input
                type="text"
                placeholder="Search by student name, parent email, child ID, or Stripe customer ID..."
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchStudent()}
                className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg pl-10 pr-4 py-2 text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-500/50"
                data-testid="student-search-input"
              />
            </div>
            <Button onClick={searchStudent} disabled={searchingStudent} className="bg-cyan-600 hover:bg-cyan-700" data-testid="student-search-btn">
              {searchingStudent ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          {studentResult && (
            <div className="mt-4 space-y-3">
              <p className="text-white/50 text-sm">Found {studentResult.results_count} record(s) for "{studentResult.query}"</p>
              {studentResult.records.map((r, i) => (
                <StudentDetailPanel key={i} record={r} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {exportData?.summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          {[
            { label: 'Total', value: exportData.summary.total_records, color: 'text-white' },
            { label: 'Complete', value: exportData.summary.complete, color: 'text-emerald-400' },
            { label: 'Partial', value: exportData.summary.partial, color: 'text-amber-400' },
            { label: 'Missing Critical', value: exportData.summary.missing_critical, color: 'text-red-400' },
            { label: 'Payment Linked', value: exportData.summary.payment_linked, color: 'text-indigo-400' },
            { label: 'Class Linked', value: exportData.summary.class_linked, color: 'text-teal-400' },
            { label: 'Safeguarding OK', value: exportData.summary.safeguarding_present, color: 'text-emerald-400' },
          ].map((stat, i) => (
            <Card key={i} className="bg-[#111] border-[#222]">
              <CardContent className="p-3 text-center">
                <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                <p className="text-[10px] text-white/40 mt-0.5">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Data Table */}
      {exportData?.records && (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              type="text"
              placeholder="Filter by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#111] border border-[#333] rounded-lg pl-10 pr-10 py-2 text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-500/50"
              data-testid="export-filter-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <p className="text-white/40 text-xs">Showing {filtered.length} of {exportData.records.length} records. Click a row to expand.</p>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#333] text-white/50 text-xs">
                  <th className="text-left py-2 px-2">Student</th>
                  <th className="text-left py-2 px-2">Parent</th>
                  <th className="text-center py-2 px-2">DOB</th>
                  <th className="text-center py-2 px-2">Card</th>
                  <th className="text-center py-2 px-2">Classes</th>
                  <th className="text-left py-2 px-2">Found In</th>
                  <th className="text-left py-2 px-2">Status</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const isExpanded = expandedRow === i;
                  const cardStr = r.cards?.length > 0
                    ? `${r.cards[0].brand} ****${r.cards[0].last4}`
                    : null;

                  return (
                    <React.Fragment key={i}>
                      <tr
                        className={`border-b border-[#222] cursor-pointer hover:bg-[#111] ${
                          r.completeness === 'missing-critical-fields' ? 'bg-red-500/5' :
                          r.completeness === 'partial' ? '' : 'bg-emerald-500/5'
                        }`}
                        onClick={() => setExpandedRow(isExpanded ? null : i)}
                        data-testid={`export-row-${i}`}
                      >
                        <td className="py-2 px-2">
                          <div className="text-white text-sm">{r.student_name?.value || '?'}</div>
                          <SourceBadge source={r.student_name?.source} />
                        </td>
                        <td className="py-2 px-2">
                          <div className="text-white/70 text-xs">{r.parent_email?.value || '?'}</div>
                          <div className="text-white/40 text-xs">{r.parent_name?.value}</div>
                        </td>
                        <td className="py-2 px-2 text-center">
                          {r.dob?.value
                            ? <Badge className="bg-emerald-500/15 text-emerald-400 text-[10px]">YES</Badge>
                            : <Badge className="bg-red-500/15 text-red-400 text-[10px]">NO</Badge>
                          }
                        </td>
                        <td className="py-2 px-2 text-center">
                          {cardStr
                            ? <div className="flex items-center justify-center gap-1"><CreditCard className="w-3 h-3 text-indigo-400" /><span className="text-white/60 text-xs">{cardStr}</span></div>
                            : <span className="text-white/20 text-xs">-</span>
                          }
                        </td>
                        <td className="py-2 px-2 text-center text-white/60">{r.classes?.length || 0}</td>
                        <td className="py-2 px-2">
                          <div className="flex flex-wrap gap-0.5">
                            {Object.entries(r.found_in || {}).filter(([,v]) => v).map(([k]) => (
                              <Badge key={k} className="text-[9px] px-1 py-0 bg-white/5 text-white/40 border border-white/10">{k.replace('_', ' ')}</Badge>
                            ))}
                          </div>
                        </td>
                        <td className="py-2 px-2"><CompletenessIndicator completeness={r.completeness} flags={r.flags} /></td>
                        <td className="py-2 px-2 text-white/30">
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={8} className="p-3 bg-[#0a0a0a]">
                            <StudentDetailPanel record={r} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default DataExportTab;
